import { startCase } from 'es-toolkit/compat';
import type {
  ImprovementsProgressItemModel,
  ImprovementsProgressModel,
  MaintenanceMediaModel,
  MaintenanceOwnerResourceModel,
  SpaceListingModel,
} from 'api/models';
import { LABEL_COLOR_TYPES } from 'components/Label/Label';
import type { LabelColorType } from 'components/Label/labelTypes';
import { POST_INSPECTION_FLOW_STRINGS } from 'strings/post-inspection-flow';

type Trade = NonNullable<ImprovementsProgressItemModel['trade']>;
type PreviousReportSubType = ImprovementsProgressModel['previousReportSubType'];

const TRADE_LABEL_OVERRIDES: Partial<Record<Trade, string>> = {
  DoorsAndWindows: 'Doors & Windows',
  DrywallAndPlaster: 'Drywall & Plaster',
  GardeningAndLandscaping: 'Gardening & Landscaping',
  KeysAndAccess: 'Keys & Access',
  MasonryAndConcrete: 'Masonry & Concrete',
  RemediationAndRestoration: 'Remediation & Restoration',
  CabinetsCountertops: 'Cabinets & Countertops',
  AirConditioningHeating: 'Air Conditioning & Heating',
};

const SECTION_TITLES: Partial<
  Record<NonNullable<PreviousReportSubType>, string>
> = {
  ImprovementsRepairsInspection:
    POST_INSPECTION_FLOW_STRINGS['progress.section.title.onboarding'],
  ImprovementsMoveOutInspection:
    POST_INSPECTION_FLOW_STRINGS['progress.section.title.move_out'],
};

export function getSectionTitle(subType: PreviousReportSubType): string {
  const title = subType ? SECTION_TITLES[subType] : undefined;
  return title ?? POST_INSPECTION_FLOW_STRINGS['progress.section.title'];
}

export function getTradeLabel(trade: Trade | undefined): string {
  if (!trade) return '';
  return TRADE_LABEL_OVERRIDES[trade] ?? startCase(trade);
}

export function getRoomLabels(
  owners: MaintenanceOwnerResourceModel[] | undefined,
  spaces: SpaceListingModel[] | undefined,
): string[] {
  if (!owners?.length || !spaces?.length) return [];

  return owners
    .filter((owner) => owner.ownerType === 'Space' && !!owner.ownerId)
    .map((owner) => spaces.find((space) => space.id === owner.ownerId)?.type)
    .filter((type): type is string => !!type);
}

const IN_PROGRESS_BADGE = {
  label: POST_INSPECTION_FLOW_STRINGS['progress.status.in_progress'],
  color: LABEL_COLOR_TYPES.BLUE,
};

const STATUS_BADGES: Record<
  NonNullable<ImprovementsProgressItemModel['status']>,
  { label: string; color: LabelColorType }
> = {
  Closed: {
    label: POST_INSPECTION_FLOW_STRINGS['progress.status.completed'],
    color: LABEL_COLOR_TYPES.GREEN,
  },
  InProgress: IN_PROGRESS_BADGE,
  New: IN_PROGRESS_BADGE,
};

export function getStatusBadge(
  status: ImprovementsProgressItemModel['status'],
) {
  return status ? STATUS_BADGES[status] : undefined;
}

const RENDERABLE_MEDIA_TYPES: MaintenanceMediaModel['mediaType'][] = [
  'Image',
  'Video',
];

export function getDisplayableMedia(
  media: ImprovementsProgressItemModel['media'],
): MaintenanceMediaModel[] {
  return (
    media?.filter(
      (item) =>
        !!item.url &&
        RENDERABLE_MEDIA_TYPES.includes(item.mediaType) &&
        item.subOwnerVisibility !== 'Internal',
    ) ?? []
  );
}

export function groupMediaByStage(media: MaintenanceMediaModel[]) {
  return {
    before: media.filter((m) => m.stage === 'Before'),
    during: media.filter((m) => m.stage === 'During'),
    after: media.filter((m) => m.stage === 'After'),
  };
}
